<template>
    <div class="space-y-6 bg-white rounded-md shadow-sm border border-gray-200">
        <div class="sm:col-span-6 sm:flex justify-between items-center border-b border-gray-200 py-4 px-4">
            <div>
                <h4 class="text-xl font-bold text-gray-700 sm:text-2xl sm:truncate">My Information</h4>
                <p class="text-sm text-gray-500">Please provide your basic personal information.</p>
            </div>
            <div>
                <button v-if="recent_application && !application.personal_information_status" type="button" class="primaryButton" @click="autoFillDateFromPreviousApplication">
                    <font-awesome-icon icon="fa-rotate-right" class="pt-1 pr-2" /> Autofill From Previous Application
                </button>
            </div>
        </div>
        <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 p-4 pt-0">
            <div class="sm:col-span-2">
                <label for="first_name" v-bind:class="(form.errors.first_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    First Name <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="first_name" v-model="form.first_name" :disabled="application.status" type="text" @blur="validateField('first_name')" v-bind:class="(form.errors.first_name)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'" :class="application.status ? 'bg-gray-100' : ''">
                    <p v-text="form.errors.first_name" class="text-red-500 text-sm"></p>
                </div>
            </div>
            
            <div class="sm:col-span-2">
                <label for="middle_name" v-bind:class="(form.errors.middle_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Middle Name
                </label>
                <div class="mt-1">
                    <input id="middle_name" :disabled="disabled" v-model="form.middle_name" type="text" :class="{'bg-gray-50 cursor-not-allowed': disabled}" v-bind:class="(form.errors.middle_name)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                    <p v-text="form.errors.middle_name" class="text-red-500 text-sm"></p>
                </div>
            </div>
            
            <div class="sm:col-span-2">
                <label for="last_name" v-bind:class="(form.errors.last_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Last Name <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="last_name" v-model="form.last_name" :disabled="application.status" type="text" @blur="validateField('last_name')" v-bind:class="(form.errors.last_name)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'" :class="application.status ? 'bg-gray-100' : ''">
                    <p v-text="form.errors.last_name" class="text-red-500 text-sm"></p>
                </div>
            </div>

            <div class="sm:col-span-2">
                <label for="phone" v-bind:class="(form.errors.phone)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Phone <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="phone" v-model="form.phone" :disabled="application.status" type="text" @blur="validateField('phone')" v-bind:class="(form.errors.phone)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'" :class="application.status ? 'bg-gray-100' : ''">
                    <p v-text="form.errors.phone" class="text-red-500 text-sm"></p>
                </div>
            </div>
            
            <div class="sm:col-span-2">
                <label for="dob" v-bind:class="(form.errors.dob)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Birthday <span class="text-red-700">*</span>
                </label>
                <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute left-0 pl-3 flex items-center pointer-events-none" v-bind:class="(form.errors.dob)?'top-3':'inset-y-0'">
                        <font-awesome-icon :icon="faCalendar" class="text-gray-400" />
                    </div>
                    <input id="dob" v-model="form.dob" :disabled="application.status" type="date" @blur="validateField('dob')" v-bind:class="(form.errors.dob)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md pl-10'" :class="application.status ? 'bg-gray-100' : ''">
                    <p v-text="form.errors.dob" class="text-red-500 text-sm"></p>
                </div>
            </div>
            
            <div class="sm:col-span-2">
                <label for="ssn" v-bind:class="(form.errors.ssn)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    SSN (Last 4 digits) <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="ssn" v-model="form.ssn" :disabled="application.status" type="password" @blur="validateField('ssn')" v-bind:class="(form.errors.ssn)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'" :class="application.status ? 'bg-gray-100' : ''">
                    <p v-text="form.errors.ssn" class="text-red-500 text-sm"></p>
                </div>
            </div>
            
            <div v-if="applicant_type && applicant_type.type === 'main'" class="sm:col-span-2">
                <label for="desired_move_in_date" v-bind:class="(form.errors.desired_move_in_date)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Desired Move-in Date <span class="text-red-700">*</span>
                </label>
                <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute left-0 pl-3 mt-2 pointer-events-none">
                        <font-awesome-icon :icon="faCalendar" class="text-gray-400" />
                    </div>
                    <input id="desired_move_in_date" v-model="form.desired_move_in_date" type="date" @blur="validateField('desired_move_in_date')" v-bind:class="(form.errors.desired_move_in_date)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md pl-10'">
                    <p v-text="form.errors.desired_move_in_date" class="text-red-500 text-sm"></p>
                </div>
            </div>
            
            <div v-if="applicant_type && applicant_type.type === 'main'" class="sm:col-span-2">
                <label for="desired_lease_term" v-bind:class="(form.errors.desired_lease_term)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Desired Lease Term <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <select id="desired_lease_term" v-model="form.desired_lease_term" @blur="validateField('desired_lease_term')" v-bind:class="(form.errors.desired_lease_term)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm'">
                        <option value="">-- SELECT --</option>
                        <option value="12-months">12 Months</option>
                        <option value="24-months">24 Months</option>
                        <option value="36-months">36 Months</option>
                    </select>
                    <p v-text="form.errors.desired_lease_term" class="text-red-500 text-sm"></p>
                </div>
            </div>
            <div class="sm:col-span-2">
                <label for="source_of_income_worked_experience" class="block text-sm font-medium text-gray-700">
                    Monthly Rent Amount
                </label>
                <div class="mt-2 md:ml-3">
                    <span class="text-gray-900 font-semibold">{{ formatCurrency(property.rent) }}</span>
                </div>
            </div>

            <div class="sm:col-span-6 hidden">
                <alert-card type="warning">
                    <p>Some rental markets are competitive and rent can escalate. If you are willing to pay any additional amount of monthly rent on top of the list price, please add that here.</p>
                </alert-card>
            </div>
            <div class="sm:col-span-3">
                <span class="flex">
                    <label for="willing_rent" v-bind:class="(form.errors.willing_rent)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                        I'm willing to increase the monthly rent for this property
                    </label>
                    <tool-tip
                        title="Competitive Rent"
                        description="In a competitive market some applicants are willing to pay an additional amount per month over the advertised list price. Enter that additional amount in this space i.e. (100, 200, 500 etc)."
                    ></tool-tip>
                </span>
                <div class="mt-1">
                    <input id="willing_rent" v-model="form.willing_rent" :error="form.errors.willing_rent" type="number" step="0.01" v-bind:class="(form.errors.willing_rent)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                    <p v-text="form.errors.willing_rent" class="text-red-500 text-sm"></p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCalendar } from '@fortawesome/pro-light-svg-icons'


import AlertCard from '@/Shared/AlertCard.vue';
import ToolTip from '@/Shared/ToolTip.vue';


export default {
    components: {
        ToolTip,
        AlertCard,
    FontAwesomeIcon
  },
    props: {
        form: Object,
        application: Object,
        recent_application: Object,
        property: Object,
        applicant_type: Object,
        validateField: Function,
    },
    data() {
        return {
            disabled: false,
        }
    },
    methods: {
        toggleMiddleName() {
            this.disabled = !this.disabled;
            this.form.middle_name = this.disabled ? '' : this.form.middle_name;
        },
        autoFillDateFromPreviousApplication() {
            if (this.recent_application) {
                this.form.first_name = this.recent_application.first_name || '';
                this.form.middle_name = this.recent_application.middle_name || '';
                this.form.last_name = this.recent_application.last_name || '';
                // ... copy other fields as needed
            }
        },
        formatCurrency(amount) {
            if (!amount) {
                amount = 0;
            }
            amount = parseFloat(amount);
            return amount.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
            })
        },
    }
}
</script>